import { styled, useTheme } from '@mui/material/styles';
import { Box, Container, Typography, Grid } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SportsIcon from '@mui/icons-material/Sports';
import { staggerContainer, fadeUp, reducedMotionVariants } from '../../../theme/animations';
import { SectionTitle, SectionSubtitle } from './SectionHeader';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  background: theme.palette.background.default,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const StepCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(4),
  borderRadius: 20,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: theme.shadows[8],
  },
}));

const IconCircle = styled(Box)<{ bgcolor: string }>(({ bgcolor, theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  backgroundColor: bgcolor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  position: 'relative',
}));

const StepBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -8,
  right: -8,
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: theme.palette.background.paper,
  border: `2px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 700,
  fontSize: '0.75rem',
  color: theme.palette.text.primary,
}));

const StepTitle = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 700,
  fontSize: '1.25rem',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1.5),
}));

const StepDescription = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
}));

function HowItWorksSectionInner() {
  const theme = useTheme();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = shouldReduceMotion ? reducedMotionVariants : staggerContainer;
  const itemVariants = shouldReduceMotion ? reducedMotionVariants : fadeUp;

  const steps = [
    {
      number: 1,
      title: 'Search',
      description: 'Browse venues by sport, location, and availability',
      Icon: SearchIcon,
      iconBg: `rgba(99, 102, 241, 0.15)`,
      iconColor: theme.palette.secondary.main,
    },
    {
      number: 2,
      title: 'Book',
      description: 'Reserve your slot instantly with secure online payment',
      Icon: EventAvailableIcon,
      iconBg: `rgba(16, 185, 129, 0.15)`,
      iconColor: theme.palette.success.main,
    },
    {
      number: 3,
      title: 'Play',
      description: 'Show up, check in, and enjoy your game',
      Icon: SportsIcon,
      iconBg: `rgba(245, 158, 11, 0.15)`,
      iconColor: theme.palette.warning.main,
    },
  ];

  return (
    <SectionContainer id="how-it-works">
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div variants={itemVariants}>
            <SectionTitle>How It Works</SectionTitle>
            <SectionSubtitle>
              Getting on the court has never been easier. Three simple steps stand between you and
              your next game.
            </SectionSubtitle>
          </motion.div>

          <Grid container spacing={4} sx={{ position: 'relative' }}>
            {steps.map((step) => {
              const { Icon } = step;
              return (
                <Grid size={{ xs: 12, md: 4 }} key={step.number} sx={{ position: 'relative' }}>
                  <motion.div variants={itemVariants}>
                    <StepCard>
                      <Box sx={{ position: 'relative', mb: 2 }}>
                        <IconCircle bgcolor={step.iconBg}>
                          <Icon sx={{ fontSize: 36, color: step.iconColor }} />
                          <StepBadge>{step.number}</StepBadge>
                        </IconCircle>
                      </Box>
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </StepCard>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </motion.div>
      </Container>
    </SectionContainer>
  );
}

export function HowItWorksSection() {
  return <HowItWorksSectionInner />;
}
